import { useCallback } from 'react';
import { useInfiniteQuery, useMutation } from 'react-query';
import { useAxios } from '../context/AxiosContext';

export interface MarketingCommentedBy {
  id: string;
  fullName: string;
}
export type MarketingCommentChangeHandler = (marketingComment: MarketingComment) => void;

export interface MarketingComment {
  id: string;
  comment: string;
  by: MarketingCommentedBy;
  createdAt: string;
}

export interface MarketingCommentsPage {
  marketingCommentsGroup: MarketingComment[];
  totalComments: number;
}

export interface UpdateMarketingCommentProps {
  commentValue: string;
  commentId: string;
}

export interface MarketingMutationResponse {
  success?: boolean;
  errors?: string[];
}

interface UseGetMarketingCommentsProps {
  profileId: string;
  enabled?: boolean;
  onSuccess: (data: any) => void;
}

export function useGetMarketingComments({ profileId, enabled = false, onSuccess }: UseGetMarketingCommentsProps) {
  const { authAxios } = useAxios();
  const NUMBER_OF_COMMENTS: number = 5;
  const fetchComments = useCallback(
    async ({ pageParam = 0, profileId }: { pageParam: number; profileId: string }) => {
      const response = await authAxios.get<MarketingCommentsPage>('/marketing-comments', {
        params: {
          profileId,
          skip: pageParam * NUMBER_OF_COMMENTS,
          limit: NUMBER_OF_COMMENTS
        }
      });
      return response?.data || { marketingCommentsGroup: [], totalComments: 0 };
    },
    [authAxios]
  );

  return useInfiniteQuery<MarketingCommentsPage>(
    ['comments', profileId],
    ({ pageParam }) => fetchComments({ pageParam, profileId }),
    {
      getNextPageParam: (_lastPage, pages) => {
        return pages.length;
      },
      onSuccess,
      refetchOnWindowFocus: false,
      enabled,
      initialData: {
        pages: [],
        pageParams: []
      }
    }
  );
}

export function useMarketingCommentsMutation(profileId: string) {
  const { authAxios } = useAxios();

  const addMarketingComment = useCallback(
    async (commentValue: string) => {
      const response = await authAxios.post<MarketingMutationResponse>('/marketing-comments', {
        profileId,
        commentValue
      });
      return response.data;
    },
    [authAxios, profileId]
  );

  const updateMarketingComment = useCallback(
    async ({ commentId, commentValue }: UpdateMarketingCommentProps) => {
      const response = await authAxios.put<MarketingMutationResponse>('/marketing-comments', {
        commentId,
        commentValue
      });
      return response.data;
    },
    [authAxios]
  );

  const deleteMarketingComment = useCallback(
    async (commentId: string) => {
      const response = await authAxios.delete<MarketingMutationResponse>('/marketing-comments', {
        params: {
          commentId
        }
      });
      return response.data;
    },
    [authAxios]
  );

  const addMarketingCommentMutation = useMutation(addMarketingComment);
  const updateMarketingCommentMutation = useMutation(updateMarketingComment);
  const deleteMarketingCommentMutation = useMutation(deleteMarketingComment);

  return {
    addMarketingComment: addMarketingCommentMutation.mutateAsync,
    updateMarketingComment: updateMarketingCommentMutation.mutateAsync,
    deleteMarketingComment: deleteMarketingCommentMutation.mutateAsync,
    mutationLoading:
      updateMarketingCommentMutation.isLoading ||
      addMarketingCommentMutation.isLoading ||
      deleteMarketingCommentMutation.isLoading
  };
}
